import React, { forwardRef, useImperativeHandle, useRef } from 'react'

import { isEmpty } from 'ramda'
import styled from 'styled-components'
import { v4 } from 'uuid'

import { TextareaProps } from '@customTypes'
import { MaskGuard, LabelGuard } from '@customTypes/components/_helpers'
import { HelpText, InputWrapper } from '_atoms'
import { buildObject } from '_helpers/data'
import { noop } from '_helpers/functions'

import { Label, IconController, StyledTextarea } from './components'
import { isIcon, isLabelProps, setNativeValue } from './helpers'

type Guards = LabelGuard & MaskGuard

const TextareaComponent: React.FC<TextareaProps & Guards> = forwardRef<
  HTMLTextAreaElement,
  Omit<TextareaProps, 'ref'> & Guards
>((props, ref) => {
  const input = useRef<HTMLTextAreaElement>(null)
  useImperativeHandle(ref, () => input.current as HTMLTextAreaElement)

  const { value, icon, iconPlacement, helpText, errorText } = props

  const isActive = !!value
  const hasIcon = isIcon(icon) && iconPlacement === 'left'

  const onInputClear: React.MouseEventHandler<HTMLButtonElement> = () => {
    const { onClear = noop } = props
    onClear()

    const element = input.current

    if (element) {
      setNativeValue(element, '')
      element.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = e => {
    const { onPressEnter, onKeyDown } = props

    if (e.key === 'Enter' && onPressEnter) onPressEnter(e)
    if (onKeyDown) onKeyDown(e)
  }

  const onInputChange: React.ChangeEventHandler<HTMLTextAreaElement> = e => {
    const { onChange, onChangeValue } = props

    if (onChange) onChange(e)
    if (onChangeValue) onChangeValue(e.target.value)
  }

  const labelProps = buildObject(props, {
    copy: ['renderLabel', 'label'],
    rename: {
      id: 'htmlFor',
    },
  })
  const iconProps = buildObject(props, {
    copy: [
      'icon',
      'iconSize',
      'iconGroup',
      'iconPlacement',
      'iconClassName',
      'iconControllerClassName',
      'onClickIcon',
      'value',
      'renderIcon',
    ],
    rename: {
      isClearable: 'clearable'
    },
    added: {
      onClear: onInputClear,
    },
  })
  const wrapperProps = buildObject(props, {
    copy: [
      'aria-label',
      'iconPlacement',
      'className',
      'isDisabled',
      'style',
    ],
  })
  const inputProps = buildObject(props, {
    copy: true,
    rename: {
      isDisabled: 'disabled'
    },
    // Remove unknown event handler warning
    remove: ['onPressEnter', 'onClear', 'onChange', 'onInput', 'onClickIcon', 'className'],
    defaultValues: {
      hasError: !!errorText && !isEmpty(errorText)
    }
  })

  const errorArr = errorText && Array.isArray(errorText) ? errorText : [errorText]
  const helpArr = helpText && Array.isArray(helpText) ? helpText : [helpText]

  return (
    <InputWrapper {...wrapperProps}>
      <IconController {...iconProps} />
      <StyledTextarea
        {...inputProps}
        onKeyDown={handleKeyDown}
        onChange={onInputChange}
        ref={input}
      />
      {isLabelProps(labelProps) && (
        <Label active={isActive} hasIcon={hasIcon} {...labelProps} />
      )}
      {errorArr?.map(err => err && <HelpText key={v4()} text={err} hasError />)}
      {helpArr?.map(text => text && <HelpText key={v4()} text={text} />)}
    </InputWrapper>
  )
})


export const Textarea = styled(TextareaComponent)``

Textarea.displayName = 'CentivoTextarea'

